import { memo, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import { ScrollArea } from "@/components/ui/scroll-area";
import { rehypeHighlightTerm } from "@/lib/messages/search-highlight";
import { cn } from "@/lib/utils";

/**
 * A pipe table wrapped in ``` … ``` is a single code block in CommonMark.
 * When the whole message is only that fence and every non-empty line is a pipe row, unwrap so GFM can parse it.
 */
function unwrapFencedPipeTableMarkdown(markdown) {
  const trimmed = String(markdown).trim();
  const open = trimmed.match(/^```(?:[a-zA-Z0-9_-]+)?\s*\r?\n/);
  if (!open || !trimmed.endsWith("```")) {
    return markdown;
  }
  const innerStart = open[0].length;
  const innerEnd = trimmed.length - 3;
  const inner = trimmed.slice(innerStart, innerEnd).replace(/\r\n/g, "\n").trimEnd();
  const dataLines = inner
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (dataLines.length < 2) {
    return markdown;
  }
  const isPipeRow = (line) => /^\|/.test(line) && /\|$/.test(line);
  if (!dataLines.every(isPipeRow)) {
    return markdown;
  }
  return inner;
}

/** Presentation lives in `app.css` under `.message-markdown` — avoid theme-token utilities here. */
const MESSAGE_MARKDOWN_COMPONENTS = {
  p: (props) => <p {...props} />,
  ul: (props) => <ul {...props} />,
  ol: (props) => <ol {...props} />,
  li: (props) => <li {...props} />,
  strong: (props) => <strong {...props} />,
  em: (props) => <em {...props} />,
  a: (props) => <a target="_blank" rel="noopener noreferrer" {...props} />,
  blockquote: (props) => <blockquote {...props} />,
  hr: (props) => <hr {...props} />,
  /* Step tags down one level (# → h2) for valid outlines inside chat fragments. */
  h1: ({ className, ...props }) => <h2 className={cn("msg-md-h", "msg-md-h1", className)} {...props} />,
  h2: ({ className, ...props }) => <h3 className={cn("msg-md-h", "msg-md-h2", className)} {...props} />,
  h3: ({ className, ...props }) => <h4 className={cn("msg-md-h", "msg-md-h3", className)} {...props} />,
  h4: ({ className, ...props }) => <h5 className={cn("msg-md-h", "msg-md-h4", className)} {...props} />,
  h5: ({ className, ...props }) => <h6 className={cn("msg-md-h", "msg-md-h5", className)} {...props} />,
  h6: ({ className, ...props }) => <h6 className={cn("msg-md-h", "msg-md-h6", className)} {...props} />,
  pre: (props) => <pre {...props} />,
  code: ({ className, children, ...props }) => (
    <code className={className} {...props}>
      {children}
    </code>
  ),
  table: ({ children, ...props }) => (
    <ScrollArea orientation="horizontal" className="message-markdown-table-wrap">
      <table {...props}>{children}</table>
    </ScrollArea>
  ),
  thead: (props) => <thead {...props} />,
  tbody: (props) => <tbody {...props} />,
  tr: (props) => <tr {...props} />,
  th: (props) => <th {...props} />,
  td: (props) => <td {...props} />,
  /* Emitted only by the search highlighter below; styled in app.css (.chat-search-mark). */
  mark: (props) => <mark {...props} />,
};

const NO_REHYPE_PLUGINS = [];

/**
 * `highlight` marks every occurrence of a term (in-chat search). It runs as a rehype plugin on the
 * parsed tree rather than as a string substitution, so a term containing markdown punctuation
 * cannot corrupt the document and matches inside code spans or link URLs are left alone.
 *
 * MEMOIZED, and that matters: a conversation renders hundreds of these at once (the message list is
 * not virtualized), each running the full unified parse. Without the memo every unrelated re-render
 * of the page — a keystroke, a typing indicator, a WS tick — re-parsed every body on screen.
 * Callers must therefore pass `highlight` ONLY to bodies that actually contain the term (see
 * message-cluster-list), so a new search term re-parses the handful of matches instead of all of it.
 */
export const MessageBodyMarkdown = memo(function MessageBodyMarkdown({ children, className, highlight }) {
  const term = String(highlight ?? "").trim();
  // Identity-stable while the term is unchanged, so an unrelated re-render does not re-parse.
  const rehypePlugins = useMemo(
    () => (term ? [rehypeHighlightTerm(term)] : NO_REHYPE_PLUGINS),
    [term],
  );

  const text = String(children ?? "");
  if (!text.trim()) {
    return null;
  }
  const markdown = unwrapFencedPipeTableMarkdown(text);
  return (
    <div className={cn("message-markdown text-start", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={rehypePlugins}
        components={MESSAGE_MARKDOWN_COMPONENTS}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
});
